import { forwardRef } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { JarvisAvatar } from "@/components/voice/JarvisAvatar";
import { cn } from "@/lib/utils";

const ICONS = { idle: Mic, listening: Square, transcribing: Loader2, thinking: Square, speaking: Square };
const LABELS = { idle: "Tap to talk", listening: "Tap to send", transcribing: "Sending your words", thinking: "Tap to stop", speaking: "Tap to interrupt" };
const CHIP = { idle: "Ready", listening: "Listening", transcribing: "Listening", thinking: "Thinking", speaking: "Speaking" };

// Portrait card + the single tap control. --level (0–1) is set every frame by the voice session for the glow.
export const JarvisPresence = forwardRef(function JarvisPresence({ status, disabled, onTap, levelRef }, ref) {
  const Icon = ICONS[status];
  return (
    <div ref={ref} className="jarvis-presence flex flex-col items-center" data-status={status} data-testid="jarvis-presence">
      <div className="jarvis-frame relative aspect-[4/5] w-[min(74vw,300px)] overflow-hidden rounded-[28px] bg-ink-950 sm:w-[340px] lg:w-[360px]">
        <JarvisAvatar status={status} levelRef={levelRef} />
        <span className="jarvis-chip absolute left-3.5 top-3.5 z-10 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/45 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-200 backdrop-blur-md" data-testid="jarvis-avatar-state">
          <span className="jarvis-chip-dot h-1.5 w-1.5 rounded-full" />
          {CHIP[status]}
        </span>
        {status === "speaking" && (
          <span aria-hidden className="jarvis-bars absolute bottom-4 left-1/2 z-10 flex h-4 -translate-x-1/2 items-end gap-[3px]">
            {[0.6, 1, 0.75, 0.9, 0.55].map((k, i) => <span key={i} style={{ "--k": k }} />)}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onTap}
        disabled={disabled}
        aria-label={LABELS[status]}
        data-testid="voice-orb-button"
        className={cn(
          "jarvis-tap relative mt-5 flex h-16 w-16 items-center justify-center rounded-full border transition-[border-color,background-color,transform] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40",
          status === "listening" ? "border-rose-400/60 bg-rose-500/[0.12]" : status === "idle" ? "border-white/20 bg-white text-ink-950 hover:scale-[1.04]" : "border-white/15 bg-ink-850 hover:border-white/30",
        )}
      >
        <Icon className={cn("h-6 w-6", status === "idle" ? "text-ink-950" : "text-white", status === "transcribing" && "animate-spin", (status === "listening" || status === "thinking" || status === "speaking") && "h-5 w-5 fill-current")} strokeWidth={1.7} />
      </button>
    </div>
  );
});
